import { useState } from 'react';
import { Check, ChevronDown, Sparkles } from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { RoadmapMilestone, RoadmapStatus } from '../types';
import { SubmoduleChip } from './SubmoduleChip';

interface Props {
  milestone: RoadmapMilestone;
  isLast: boolean;
  index: number;
}

const STATUS_LABEL: Record<RoadmapStatus, string> = {
  live: 'Live',
  'in-progress': 'In Progress',
  planned: 'Planned',
};

const STATUS_BADGE: Record<RoadmapStatus, string> = {
  live: 'bg-capitol-red-500 text-white shadow-sm shadow-capitol-red-500/30',
  'in-progress': 'bg-warning text-warning-foreground shadow-sm shadow-warning/30',
  planned: 'bg-capitol-gray-200 text-capitol-gray-500',
};

const RAIL_BG: Record<RoadmapStatus, string> = {
  live: 'bg-gradient-to-b from-capitol-red-500 to-capitol-red-500/40',
  'in-progress': 'bg-gradient-to-b from-warning to-warning/40',
  planned: 'bg-capitol-gray-200',
};

const ICON_TINT: Record<RoadmapStatus, string> = {
  live: 'bg-capitol-red-50 text-capitol-red-500 ring-1 ring-capitol-red-100',
  'in-progress': 'bg-warning/10 text-warning ring-1 ring-warning/30',
  planned: 'bg-capitol-gray-100 text-capitol-gray-500 ring-1 ring-capitol-gray-200',
};

const TOP_STRIP: Record<RoadmapStatus, string> = {
  live: 'bg-gradient-to-r from-capitol-red-500 via-capitol-red-400 to-capitol-red-500/50',
  'in-progress': 'bg-gradient-to-r from-warning via-warning/80 to-warning/50',
  planned: 'bg-gradient-to-r from-capitol-gray-300 to-capitol-gray-200',
};

function formatDeployedAt(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function RoadmapCheckpoint({ milestone, isLast, index }: Props) {
  const [open, setOpen] = useState(milestone.status !== 'planned');
  const Icon = milestone.icon;

  return (
    <div
      className="relative pb-10 pl-16 last:pb-0"
      style={{
        animation: 'roadmap-fade-up 0.5s ease-out backwards',
        animationDelay: `${index * 80}ms`,
      }}
    >
      {!isLast && (
        <span
          aria-hidden
          className={cn(
            'absolute left-[19px] top-10 h-[calc(100%-2.5rem)] w-0.5 rounded-full',
            RAIL_BG[milestone.status]
          )}
        />
      )}

      <span
        aria-hidden
        className={cn(
          'absolute left-2 top-2 flex h-10 w-10 items-center justify-center rounded-full border-2 bg-card transition-transform duration-300',
          milestone.status === 'live' &&
            'border-capitol-red-500 shadow-[0_0_0_4px_rgba(237,28,36,0.10)]',
          milestone.status === 'in-progress' &&
            'border-warning shadow-[0_0_0_4px_rgba(251,191,36,0.18)]',
          milestone.status === 'planned' && 'border-capitol-gray-300'
        )}
      >
        {milestone.status === 'live' && (
          <Check className="h-5 w-5 text-capitol-red-500" strokeWidth={3} />
        )}
        {milestone.status === 'in-progress' && (
          <span className="relative flex h-3 w-3">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warning opacity-75" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-warning" />
          </span>
        )}
        {milestone.status === 'planned' && (
          <Sparkles className="h-4 w-4 text-capitol-gray-400" />
        )}
      </span>

      <Collapsible open={open} onOpenChange={setOpen}>
        <div className="group overflow-hidden rounded-2xl border bg-card shadow-sm ring-1 ring-transparent transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:ring-capitol-red-100">
          <span aria-hidden className={cn('block h-1 w-full', TOP_STRIP[milestone.status])} />

          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="group/trigger flex w-full items-start gap-4 p-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-capitol-red-300"
            >
              <span
                className={cn(
                  'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-105',
                  ICON_TINT[milestone.status]
                )}
              >
                <Icon className="h-6 w-6" />
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-semibold leading-tight text-capitol-gray-500">
                    {milestone.name}
                  </h3>
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
                      STATUS_BADGE[milestone.status]
                    )}
                  >
                    {STATUS_LABEL[milestone.status]}
                  </span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{milestone.tagline}</p>
                <p className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                  {milestone.status === 'live' && milestone.deployedAt ? (
                    <span>
                      <span className="font-medium text-capitol-gray-500">Deployed</span>{' '}
                      {formatDeployedAt(milestone.deployedAt)}
                    </span>
                  ) : milestone.targetQuarter ? (
                    <span>
                      <span className="font-medium text-capitol-gray-500">Targeting</span>{' '}
                      {milestone.targetQuarter}
                    </span>
                  ) : null}
                  <span aria-hidden>·</span>
                  <span>{milestone.submodules.length} sub-modules</span>
                </p>
              </div>

              <ChevronDown
                aria-hidden
                className="mt-1 h-5 w-5 shrink-0 text-capitol-gray-400 transition-transform duration-300 group-data-[state=open]/trigger:rotate-180"
              />
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent className="roadmap-content overflow-hidden">
            <div className="space-y-5 px-5 pb-5">
              <div className="rounded-xl bg-muted/40 p-4">
                <p className="text-sm leading-relaxed text-capitol-gray-500">
                  {milestone.purpose}
                </p>
              </div>

              {milestone.submodules.length > 0 && (
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-capitol-gray-400">
                    What's included
                  </h4>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {milestone.submodules.map((sub) => (
                      <SubmoduleChip key={sub.code} submodule={sub} />
                    ))}
                  </div>
                </div>
              )}

              {milestone.highlights && milestone.highlights.length > 0 && (
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-capitol-gray-400">
                    Highlights
                  </h4>
                  <ul className="space-y-1.5">
                    {milestone.highlights.map((h) => (
                      <li
                        key={h}
                        className="flex items-start gap-2 text-sm text-capitol-gray-500"
                      >
                        <Check
                          aria-hidden
                          className="mt-0.5 h-4 w-4 shrink-0 text-capitol-red-500"
                          strokeWidth={2.5}
                        />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </div>
  );
}
